import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { BookOpen } from "lucide-react";
import ToastContainer, { showToast } from "./Toast";
import {
  STORAGE_KEYS, MODULES, readStorage, saveStorage, addActivity,
  getModuleCompletion, getStatusByCompletion, buildRingStyle,
  getSchoolSettings, getActiveAcademicYear, loadActivityLog, clearYearCache,
} from "./utils";
import AdminAPI from "../../services/AdminAPI";

const TAG_COLORS = {
  green: "bg-[#eaf8f1] text-[#2d9b68] border-[#cfe8dc]",
  blue: "bg-[#eef6fc] text-[#2d81c1] border-[#cfe3f3]",
  orange: "bg-[#fff4eb] text-[#f47c20] border-[#f7c9a1]",
  yellow: "bg-[#fff7e7] text-[#d58d1f] border-[#f4deab]",
  red: "bg-[#fff0f0] text-[#d95353] border-[#f4c5c5]",
};

function Tag({ cls, children }) {
  return (
    <span className={`inline-flex items-center gap-[6px] px-[10px] py-[6px] rounded-full text-[11px] font-semibold border ${TAG_COLORS[cls] || TAG_COLORS.blue}`}>
      {children}
    </span>
  );
}

function computeDashboardStats() {
  let completedModules = 0;
  let totalCompletion = 0;
  let savedRecords = 0;

  MODULES.forEach(module => {
    const percent = getModuleCompletion(module.key);
    totalCompletion += percent;
    if (percent >= 60) completedModules++;

    const data = readStorage(STORAGE_KEYS[module.key], null);
    if (data) {
      if (Array.isArray(data)) {
        savedRecords += data.length;
      } else if (typeof data === "object") {
        savedRecords += Object.values(data).filter(value => {
          if (Array.isArray(value)) return value.length > 0;
          return value !== null && value !== undefined && String(value).trim() !== "";
        }).length;
      } else {
        savedRecords++;
      }
    }
  });

  const overallPercent = Math.round(totalCompletion / MODULES.length);
  return { completedModules, pendingModules: MODULES.length - completedModules, overallPercent, savedRecords };
}

export default function Dashboard() {
  const settings = getSchoolSettings();
  const [searchQuery, setSearchQuery] = useState("");
  const [stats, setStats] = useState(computeDashboardStats());
  const [activity, setActivity] = useState(() => readStorage(STORAGE_KEYS.activity, []));
  const [, forceUpdate] = useState(0);

  const refreshAll = useCallback(() => {
    setStats(computeDashboardStats());
    setActivity(readStorage(STORAGE_KEYS.activity, []));
    forceUpdate(n => n + 1);
  }, []);

  useEffect(() => {
    refreshAll();
    // Hydrate localStorage from API bulk endpoint, then re-compute stats
    AdminAPI.sdpGetBulk(getActiveAcademicYear()).then(res => {
      if (res?.modules) {
        Object.entries(res.modules).forEach(([key, data]) => {
          if (data && STORAGE_KEYS[key]) saveStorage(STORAGE_KEYS[key], data);
        });
        setStats(computeDashboardStats());
        forceUpdate(n => n + 1);
      }
    }).catch(() => {});

    // Cache SQAAF completion so Diagnosis completion reflects SQAAF status
    const year = getActiveAcademicYear();
    Promise.all([
      AdminAPI.sqaafGetCurrentAudit(year),
      AdminAPI.sqaafGetFrameworkOverview(),
    ]).then(([audit, framework]) => {
      const domains = framework?.domains || framework || [];
      let totalIndicators = 0;
      domains.forEach(d => {
        (d.subdomains || []).forEach(sd => {
          totalIndicators += (sd.indicators || []).length;
        });
      });
      const answered = (audit?.responses || []).filter(r => Number(r?.selected_level) > 0);
      const sqaafPct = totalIndicators > 0
        ? Math.round((answered.length / totalIndicators) * 100)
        : 0;
      saveStorage(STORAGE_KEYS.sqaafCompletionCache, { percent: sqaafPct, year });
      setStats(computeDashboardStats());
      forceUpdate(n => n + 1);
    }).catch(() => {});

    loadActivityLog(100).then(log => { if (log.length > 0) setActivity(log); }).catch(() => {});
  }, [refreshAll]);

  function handleViewChange(e) {
    const label = e.target.options[e.target.selectedIndex].text;
    addActivity("Dashboard", `View changed to ${label}`, "Updated", "blue");
    setActivity(readStorage(STORAGE_KEYS.activity, []));
    showToast(`Switched to ${label}.`, "success");
  }

  function handleRefresh() {
    refreshAll();
    showToast("Dashboard refreshed.", "success");
  }

  function handleClearDemo() {
    if (!window.confirm("This will clear all locally saved portal demo data. Continue?")) return;
    clearYearCache();
    refreshAll();
    showToast("All demo portal data cleared.", "warn");
  }

  function seedDemoActivity() {
    const existing = readStorage(STORAGE_KEYS.activity, []);
    const demo = [
      { module: "Institutional Identity", action: "Vision and mission saved", status: "Saved", statusClass: "green" },
      { module: "School Information", action: "School profile drafted", status: "Draft", statusClass: "orange" },
      { module: "Goals", action: "Academic improvement goal added", status: "Updated", statusClass: "blue" },
    ];
    const merged = [...existing, ...demo.map(item => ({ time: new Date().toLocaleString(), ...item }))];
    saveStorage(STORAGE_KEYS.activity, merged);
    setActivity(merged);
    showToast("Demo activity added.", "success");
  }

  const filteredModules = MODULES.filter(m =>
    !searchQuery.trim() || m.name.toLowerCase().includes(searchQuery.toLowerCase()) || m.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const recentActivity = [...activity].reverse().slice(0, 8);

  return (

    <>
      <div className="flex-1 min-w-0 flex flex-col border-l-2 border-slate-200 ml-1">
        <div className="flex justify-center pl-10 pr-6 py-6">
          <main className="w-full font-[poppins]">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6 bg-[#D67C2C]">
                <BookOpen className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-5xl font-bold text-[#D67C2C] mb-4">School Development Dashboard</h1>
              <p className="text-[#1D3469] text-[18px] sm:text-[20px] font-medium max-w-5xl mx-auto px-4 mb-8 leading-[1.4]">
                Monitor school planning progress and module completion across your academic year plan.
              </p>
            </div>

            {/* Hero Panel */}
            <section className="bg-white rounded-xl shadow-md border border-gray-200 p-6 mb-8">
              <div className="grid gap-[22px]" style={{ gridTemplateColumns: "1.15fr .85fr" }}>
                <div className="bg-[#F9FAFB] border border-gray-200 rounded-xl p-5">
                  <div className="inline-flex items-center gap-2 px-3 py-[7px] rounded-full bg-[#eef6fc] border border-[#cfe3f3] text-[#2d81c1] text-[11px] font-semibold mb-[10px]">
                    School Progress Snapshot
                  </div>
                  <div className="text-[22px] font-semibold leading-[1.3] text-[#233445] mb-[10px]">
                    Build the school plan step by step, and see it come together as one connected system.
                  </div>
                  <p className="text-[13px] text-[#617587] leading-[1.7] mb-[18px] max-w-[720px]">
                    Start with institutional identity, continue through school information, diagnosis, SWOT, goals,
                    action plans, and monitoring, and finally view the whole school development structure in one compiled screen.
                  </p>
                  <div className="w-full h-3 rounded-full bg-[#edf2f8] overflow-hidden border border-[#e2e8f0] mb-[10px]">
                    <div
                      className="h-full rounded-full transition-all duration-[350ms]"
                      style={{ width: `${stats.overallPercent}%`, background: "linear-gradient(90deg, #f47c20, #2d81c1)" }}
                    />
                  </div>
                  <div className="flex items-center justify-between text-[12px] text-[#617587]">
                    <span>{stats.overallPercent}% completed</span>
                    <span>{stats.completedModules}/{MODULES.length} modules completed</span>
                  </div>
                </div>

                <div className="bg-[#F9FAFB] border border-gray-200 rounded-xl p-5">
                  <div className="text-[16px] font-semibold text-[#233445] mb-[10px]">Quick Actions</div>
                  <div className="flex flex-col gap-3">
                    {[
                      { href: "/sdp/quick-start", title: "AI Quick Start — One-Click SDP", desc: "Enter your school info and let AI draft Identity + Market Dynamics instantly.", badge: "New", badgeCls: "orange" },
                      { href: "/sdp/institutional-identity", title: "Fill Vision, Mission, Values & Culture", desc: "Begin with the school's identity and direction.", badge: "Priority", badgeCls: "orange" },
                      { href: "/sdp/goals", title: "Review School Goals", desc: "Convert findings into goals with timelines and ownership.", badge: "Planning", badgeCls: "blue" },
                      { href: "/sdp/complete-school-view", title: "Open Final Full School View", desc: "See all saved information in one complete compiled layout.", badge: "Summary", badgeCls: "green" },
                    ].map((item, i) => (
                      <Link
                        key={i}
                        to={item.href}
                        className="border border-[#e6ecf3] bg-white rounded-[18px] p-4 flex justify-between items-center gap-3 transition-all duration-200 hover:border-[#cfe3f3] hover:bg-[#eef6fc] hover:-translate-y-[1px]"
                      >
                        <div>
                          <h4 className="text-[14px] font-semibold text-[#233445] mb-1">{item.title}</h4>
                          <p className="text-[12px] text-[#617587]">{item.desc}</p>
                        </div>
                        <span className={`px-[10px] py-[7px] rounded-full text-[11px] font-semibold whitespace-nowrap ${TAG_COLORS[item.badgeCls]}`}>
                          {item.badge}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Stats Grid */}
            <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {[
                { label: "Overall Completion", icon: "📌", iconCls: "bg-[#fff4eb] text-[#f47c20] border-[#f7c9a1]", value: `${stats.overallPercent}%`, meta: "Based on all major portal modules" },
                { label: "Completed Modules", icon: "✓", iconCls: "bg-[#eaf8f1] text-[#2d9b68] border-[#cfe8dc]", value: stats.completedModules, meta: "Modules with meaningful saved data" },
                { label: "Pending Modules", icon: "⏳", iconCls: "bg-[#fff7e7] text-[#d58d1f] border-[#f4deab]", value: stats.pendingModules, meta: "Still waiting for user inputs" },
                { label: "Saved Records", icon: "🗂", iconCls: "bg-[#eef6fc] text-[#2d81c1] border-[#cfe3f3]", value: stats.savedRecords, meta: "Entries stored locally in this prototype" },
              ].map((s, i) => (
                <div key={i} className="bg-white rounded-xl shadow-md border border-gray-200 p-6 hover:shadow-lg transition-all duration-300 min-h-[130px] flex flex-col justify-between">
                  <div className="flex items-center justify-between gap-[10px] mb-4">
                    <span className="text-[12px] text-[#617587] font-medium">{s.label}</span>
                    <div className={`w-[42px] h-[42px] rounded-[14px] grid place-items-center text-[18px] border ${s.iconCls}`}>
                      {s.icon}
                    </div>
                  </div>
                  <div>
                    <div className="text-[28px] font-bold leading-none text-[#233445] mb-[6px]">{s.value}</div>
                    <div className="text-[12px] text-[#617587]">{s.meta}</div>
                  </div>
                </div>
              ))}
            </section>

            {/* Module Completion + System Notes */}
            <section className="grid gap-6 mb-8" style={{ gridTemplateColumns: "1.2fr .8fr" }}>
              <section className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                <div className="px-5 py-[18px] border-b border-[#e6ecf3] flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[16px] font-semibold text-[#233445] mb-[2px]">Module Completion Overview</div>
                    <div className="text-[12px] text-[#617587]">Live completion based on what has been entered into each page.</div>
                  </div>
                </div>
                <div className="p-5">
                  <div className="grid grid-cols-2 gap-[14px]">
                    {filteredModules.map(module => {
                      const percent = getModuleCompletion(module.key);
                      const status = getStatusByCompletion(percent);
                      return (
                        <Link
                          key={module.key}
                          to={module.file}
                          className="border border-[#e6ecf3] bg-white rounded-[18px] p-4 transition-all duration-[220ms] flex justify-between gap-3 items-start hover:bg-[#fbfdff] hover:border-[#cfe3f3] hover:-translate-y-[1px]"
                        >
                          <div>
                            <h4 className="text-[14px] font-semibold text-[#233445] mb-1">{module.name}</h4>
                            <p className="text-[12px] text-[#617587] leading-[1.6]">{module.description}</p>
                            <div className="mt-[10px]">
                              <Tag cls={status.cls}>{status.label}</Tag>
                            </div>
                          </div>
                          <div
                            className="w-[54px] h-[54px] min-w-[54px] rounded-full grid place-items-center text-[11px] font-bold relative flex-shrink-0"
                            style={{ background: buildRingStyle(percent) }}
                          >
                            <div className="absolute inset-[6px] bg-white rounded-full" />
                            <span className="relative z-10 text-[#233445]">{percent}%</span>
                          </div>
                        </Link>
                      );
                    })}
                    {filteredModules.length === 0 && (
                      <div className="col-span-2 text-center py-6 text-[13px] text-[#617587]">No modules match your search.</div>
                    )}
                  </div>
                </div>
              </section>

              <aside className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                <div className="px-5 py-[18px] border-b border-[#e6ecf3]">
                  <div className="text-[16px] font-semibold text-[#233445] mb-[2px]">System Notes</div>
                  <div className="text-[12px] text-[#617587]">How this prototype behaves.</div>
                </div>
                <div className="p-5 flex flex-col gap-[14px]">
                  {[
                    { title: "Data saving", text: "All module pages will save data in localStorage so the dashboard, reports page, and final school view can read and display it." },
                    { title: "Completion logic", text: "A module is counted as completed when its required fields or data rows are meaningfully filled, not just opened." },
                    { title: "Next step", text: "Start with Institutional Identity first, because the rest of the planning structure should logically connect to it." },
                  ].map((note, i) => (
                    <div key={i} className="border border-[#e6ecf3] bg-[#fbfcfe] rounded-[18px] p-4">
                      <h4 className="text-[14px] font-semibold text-[#233445] mb-[6px]">{note.title}</h4>
                      <p className="text-[12px] text-[#617587] leading-[1.7]">{note.text}</p>
                    </div>
                  ))}
                </div>
              </aside>
            </section>

            {/* Activity Feed + Priority Modules */}
            <section className="grid grid-cols-2 gap-6 mb-8">
              <section className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                <div className="px-5 py-[18px] border-b border-[#e6ecf3] flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[16px] font-semibold text-[#233445] mb-[2px]">Recent Activity Feed</div>
                    <div className="text-[12px] text-[#617587]">Latest saved actions in this demo portal.</div>
                  </div>
                  <button
                    onClick={seedDemoActivity}
                    className="min-h-[44px] px-4 bg-white text-[#233445] border border-[#e6ecf3] rounded-[12px] text-[13px] font-semibold hover:bg-[#eef6fc] hover:text-[#2d81c1] hover:border-[#cfe3f3] transition-colors"
                  >
                    Add Demo Activity
                  </button>
                </div>
                <div className="p-5">
                  <div className="border border-[#e6ecf3] rounded-[18px] overflow-hidden bg-white">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr>
                          {["Time", "Module", "Action", "Status"].map(h => (
                            <th key={h} className="bg-[#fafbfd] px-[14px] py-[14px] text-left text-[12px] text-[#617587] font-semibold border-b border-[#e6ecf3]">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {recentActivity.length === 0 ? (
                          <tr>
                            <td colSpan={4} className="text-center py-6 text-[13px] text-[#617587]">
                              No activity yet. Save something in the next pages and it will start showing here.
                            </td>
                          </tr>
                        ) : recentActivity.map((item, i) => (
                          <tr key={i} className="hover:bg-[#fcfdff]">
                            <td className="px-[14px] py-[14px] text-[13px] text-[#233445] border-b border-[#eef2f7]">{item.time || "-"}</td>
                            <td className="px-[14px] py-[14px] text-[13px] text-[#233445] border-b border-[#eef2f7]">{item.module || "-"}</td>
                            <td className="px-[14px] py-[14px] text-[13px] text-[#233445] border-b border-[#eef2f7]">{item.action || "-"}</td>
                            <td className="px-[14px] py-[14px] text-[13px] text-[#233445] border-b border-[#eef2f7]">
                              <Tag cls={item.statusClass || "blue"}>{item.status || "Saved"}</Tag>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>

              <section className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                <div className="px-5 py-[18px] border-b border-[#e6ecf3]">
                  <div className="text-[16px] font-semibold text-[#233445] mb-[2px]">Priority Modules</div>
                  <div className="text-[12px] text-[#617587]">Suggested flow for better planning continuity.</div>
                </div>
                <div className="p-5">
                  <div className="border border-[#e6ecf3] rounded-[18px] overflow-hidden bg-white">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr>
                          {["Order", "Module", "Why it matters"].map(h => (
                            <th key={h} className="bg-[#fafbfd] px-[14px] py-[14px] text-left text-[12px] text-[#617587] font-semibold border-b border-[#e6ecf3]">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          ["1", "Institutional Identity", "Sets direction, values, mission, and culture."],
                          ["2", "School Information", "Builds the factual school profile and baseline structure."],
                          ["3", "Diagnosis", "Shows current reality before goal setting starts."],
                          ["4", "SWOT", "Converts diagnosis into strategic thinking."],
                          ["5", "Goals & Action Plans", "Turns strategy into execution and ownership."],
                          ["6", "Monitoring", "Tracks whether the school plan is actually working."],
                        ].map(([order, module, why], i) => (
                          <tr key={i} className="hover:bg-[#fcfdff]">
                            <td className="px-[14px] py-[14px] text-[13px] text-[#233445] border-b border-[#eef2f7]">{order}</td>
                            <td className="px-[14px] py-[14px] text-[13px] text-[#233445] border-b border-[#eef2f7]">{module}</td>
                            <td className="px-[14px] py-[14px] text-[13px] text-[#617587] border-b border-[#eef2f7]">{why}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>
            </section>

            <section className="bg-white rounded-xl shadow-md border border-gray-200 p-6 mb-8">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-[#1D3469]">Dashboard Actions</h3>
                  <p className="text-sm text-gray-500">Navigate key SDP pages and manage this dashboard view.</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <Link to="/sdp/institutional-identity" className="bg-[#D67C2C] hover:bg-[#c06a25] text-white px-6 py-3 rounded-lg font-medium transition-colors">
                    Start from Identity
                  </Link>
                  <Link to="/sdp/complete-school-view" className="bg-[#1D3469] hover:bg-[#16284f] text-white px-6 py-3 rounded-lg font-medium transition-colors">
                    Final Complete View
                  </Link>
                  <button onClick={handleRefresh} className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-medium">
                    Refresh
                  </button>
                  <button onClick={handleClearDemo} className="border border-red-200 text-red-600 px-4 py-2 rounded-lg hover:bg-red-50 transition-colors font-medium">
                    Clear Demo Data
                  </button>
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>

      <ToastContainer />
    </>
  );
}
